<script setup lang="ts">
  import { computed } from 'vue'
  import { ChevronRightIcon, XMarkIcon } from '@heroicons/vue/24/outline'
  import { User, Building2, Landmark, Link2 } from 'lucide-vue-next'
  import type { ActaportPartyWithMatches } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'

  const props = defineProps<{
    party: ActaportPartyWithMatches
    isSelected: boolean
    isLinked: boolean
    isNewBeteiligte?: boolean
  }>()

  const emit = defineEmits<{
    (e: 'select'): void
    (e: 'close'): void
  }>()

  const { t } = useTranslation({
    en: {
      potentialMatches: '{count} Potential Matches',
      potentialMatch: '1 Potential Match',
      linkedToActaport: 'Linked to Actaport',
      newBeteiligte: 'New Beteiligte',
      email: 'Email',
      phoneNumber: 'Phone number',
      dateOfBirth: 'Date of Birth',
      address: 'Address'
    },
    de: {
      potentialMatches: '{count} Potenzielle Treffer',
      potentialMatch: '1 Potenzieller Treffer',
      linkedToActaport: 'Mit Actaport verknüpft',
      newBeteiligte: 'Neue Beteiligte',
      email: 'E-Mail',
      phoneNumber: 'Telefonnummer',
      dateOfBirth: 'Geburtsdatum',
      address: 'Adresse'
    }
  })

  const displayRoles = computed(() => {
    return props.party.entity_roles
  })

  const isCompany = computed(() => {
    return props.party.entity_type === 'legal_person'
  })

  const isInstitution = computed(() => {
    return props.party.entity_type === 'institution'
  })

  const badgeText = computed(() => {
    if (props.isNewBeteiligte) {
      return t('newBeteiligte')
    }
    if (props.isLinked) {
      return t('linkedToActaport')
    }
    if (props.party.match_count === 1) {
      return t('potentialMatch')
    }
    return t('potentialMatches', { count: props.party.match_count })
  })

  const badgeColor = computed(() => {
    if (props.isNewBeteiligte) {
      return 'bg-purple-100 text-purple-800'
    }
    if (props.isLinked) {
      return 'bg-blue-100 text-blue-800'
    }
    return 'bg-orange-100 text-orange-800'
  })

  const formattedDateOfBirth = computed(() => {
    if (!props.party.entity_date_of_birth) return ''
    const date = new Date(props.party.entity_date_of_birth)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  })
</script>

<template>
  <div
    class="relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md"
    :style="{
      backgroundColor: isSelected ? '#FAF5FF' : '#FFFFFF',
      borderColor: isSelected ? '#C084FC' : '#E5E7EB'
    }"
    @click="emit('select')"
  >
    <button
      class="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      :class="{ invisible: !isSelected }"
      @click.stop="emit('close')"
    >
      <XMarkIcon class="h-5 w-5" />
    </button>

    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 pt-2">
        <Building2
          v-if="isCompany"
          class="h-5 w-5 text-gray-600"
        />
        <Landmark
          v-else-if="isInstitution"
          class="h-5 w-5 text-gray-600"
        />
        <User
          v-else
          class="h-5 w-5 text-gray-600"
        />
      </div>

      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-2 pr-8">
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold text-gray-900">
              {{ party.entity_name }}
            </h3>
            <p class="text-sm text-gray-500">
              {{ displayRoles.join(', ') }}
            </p>
          </div>

          <span
            :class="[
              'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
              badgeColor
            ]"
          >
            <Link2
              v-if="isLinked"
              class="h-3 w-3"
            />
            <svg
              v-if="isNewBeteiligte"
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.66667 14.6666H12C12.3536 14.6666 12.6928 14.5261 12.9428 14.2761C13.1929 14.026 13.3333 13.6869 13.3333 13.3333V4.66659L10 1.33325H4C3.64638 1.33325 3.30724 1.47373 3.05719 1.72378C2.80714 1.97382 2.66667 2.31296 2.66667 2.66659V5.33325M9.33333 1.33325V3.99992C9.33333 4.35354 9.47381 4.69268 9.72386 4.94273C9.97391 5.19278 10.313 5.33325 10.6667 5.33325H13.3333M2 9.99992L3.33333 11.3333L6 8.66659"
                stroke="#6B21A8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ badgeText }}
          </span>
        </div>

        <div class="mt-3 space-y-1 text-sm">
          <div
            v-if="party.entity_email"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('email') }}:</span>
            <span class="truncate text-gray-900">{{ party.entity_email }}</span>
          </div>
          <div
            v-if="party.entity_phone"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('phoneNumber') }}:</span>
            <span class="text-gray-900">{{ party.entity_phone }}</span>
          </div>
          <div
            v-if="formattedDateOfBirth"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('dateOfBirth') }}:</span>
            <span class="text-gray-900">{{ formattedDateOfBirth }}</span>
          </div>
          <div
            v-if="party.entity_address"
            class="flex gap-1"
          >
            <span class="flex-shrink-0 text-gray-500">{{ t('address') }}:</span>
            <span class="text-gray-900">{{ party.entity_address }}</span>
          </div>
        </div>
      </div>

      <ChevronRightIcon
        v-if="!isSelected"
        class="h-5 w-5 flex-shrink-0 text-gray-400"
      />
    </div>
  </div>
</template>
